import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex justify-start items-center text-primary-color-600" }
const _hoisted_2 = { class: "flex items-center justify-center mr-4 h-12 w-12 rounded-full bg-primary-color-100" }
const _hoisted_3 = {
  key: 0,
  class: "text-lg leading-6 font-medium text-gray-900"
}
const _hoisted_4 = { class: "flex flex-col gap-y-[30px]" }
const _hoisted_5 = { class: "flex gap-x-4 w-full" }
const _hoisted_6 = { class: "w-1/2" }
const _hoisted_7 = { class: "flex gap-x-4 w-full" }
const _hoisted_8 = { class: "w-[256px]" }
const _hoisted_9 = { class: "w-[256px]" }
const _hoisted_10 = {
  key: 0,
  class: "flex flex-col gap-y-[30px]"
}
const _hoisted_11 = { class: "flex flex-col gap-y-3" }
const _hoisted_12 = { class: "flex justify-between" }
const _hoisted_13 = {
  key: 1,
  class: "flex flex-col gap-y-3"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = {
  key: 0,
  class: "flex flex-col gap-y-3"
}
const _hoisted_16 = { class: "w-full text-sm font-medium flex flex-col" }
const _hoisted_17 = { class: "border-t border-x border-gray-300 rounded-md py-2.5 px-3 text-sm font-medium text-gray-900" }
const _hoisted_18 = ["innerHTML"]
const _hoisted_19 = { key: 1 }
const _hoisted_20 = {
  key: 0,
  class: "flex justify-between w-full"
}
const _hoisted_21 = { class: "flex justify-end w-full" }
const _hoisted_22 = {
  key: 1,
  class: "flex justify-end"
}

import { computed, ref } from "vue"
import { useRoute } from "vue-router"
import {
    hasErrorMessage,
    hasUserOnlyReadPermission,
    isInvalid,
    renderTemplate,
    stripHtml,
} from "@/shared/utils/helpers"
import { DocumentTextIcon, ExternalLinkIcon } from "@heroicons/vue/outline"
import Quill, { RangeStatic } from "quill"
import GgmsMergeFields from "@/components/GgmsMergeFields.vue"
import GgmsModal from "@/components/GgmsModal.vue"
import { Template, TemplatePayload } from "@/shared/models/template"
import GgmsMultiSelect from "@/components/GgmsMultiSelect.vue"
import GgmsTextarea from "@/components/GgmsTextarea.vue"
import GgmsEmojiPicker from "@/components/GgmsEmojiPicker.vue"
import GgmsToggle from "@/components/GgmsToggle.vue"
import GgmsDropdown from "@/components/GgmsDropdown.vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import GgmsRichTextEditor from "@/components/GgmsRichTextEditor.vue"
import { useTemplateStore } from "@/stores/template"
import { useConfigStore } from "@/stores/config"
import { useAgencyStore } from "@/stores/agency"
import { useMessageThemeStore } from "@/stores/message-theme"
import { useTagStore } from "@/stores/tag"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { GgmsError } from "@/shared/services/client"
import { Agency, AgencyDomain } from "@/shared/models/agency"
import { Tag } from "@/shared/models/tag"
import { Lead } from "@/shared/models/lead"
import { Agent } from "@/shared/models/agent"

interface Props {
    open: boolean
    isCreateTemplate: boolean
    isPreviewOpen: boolean
    newTemplate: Template
    isDomainsDisabled: boolean
    domains: AgencyDomain[]
    selectedDomains: AgencyDomain[]
    selectedTags: Tag[]
    tags: Tag[]
    showDeleteButton?: boolean
    typeDropdownDisabled?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GgmsTemplateModal',
  props: {
    open: { type: Boolean },
    isCreateTemplate: { type: Boolean },
    isPreviewOpen: { type: Boolean },
    newTemplate: {},
    isDomainsDisabled: { type: Boolean },
    domains: {},
    selectedDomains: {},
    selectedTags: {},
    tags: {},
    showDeleteButton: { type: Boolean, default: true },
    typeDropdownDisabled: { type: Boolean, default: false }
  },
  emits: [
    "closeModal",
    "openPreview",
    "closePreview",
    "update:newTemplate",
    "openDelete",
    "update:selectedDomains",
    "update:selectedTags",
    "saveTemplate",
],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const route = useRoute()
const templateStore = useTemplateStore()
const configStore = useConfigStore()
const agencyStore = useAgencyStore()
const messageThemeStore = useMessageThemeStore()
const tagStore = useTagStore()
const gridCollectionStore = useGridCollectionStore()

const localNewTemplate = computed({
    get() {
        return props.newTemplate
    },
    set(value) {
        emit("update:newTemplate", value)
    },
})

const localSelectedDomains = computed({
    get() {
        return props.selectedDomains
    },
    set(value) {
        emit("update:selectedDomains", value)
    },
})

const localSelectedTags = computed({
    get() {
        return props.selectedTags
    },
    set(value) {
        emit("update:selectedTags", value)
    },
})

const agency = computed(() => agencyStore.agency)
const messagePreviewText = ref("")
const emailPreviewText = ref("")
const emailPreviewSubject = ref("")

const createModalTitle = computed(() => (props.isCreateTemplate ? "New" : "Update"))
const createModalButtonText = computed(() => (props.isCreateTemplate ? "Save" : "Update"))

const isViewingFormatted = ref(true)
const isTemplateTypeEmail = computed(() => localNewTemplate.value.templateType === "email")
const isEmailBodyFocused = ref(true)
const isPreviewAvailable = computed(
    () => !!(localNewTemplate.value.body?.length || localNewTemplate.value.textBody?.length)
)
let lastSelection = {} as RangeStatic

const quill = ref<Quill>()
const tempVarForSubject = ref("")
const tempVarForBodyText = ref("")

const modalData = ref({
    listName: "",
    listDescription: "",
    isPinned: false,
    isInputVisible: false,
})

const isLoading = ref(false)
const listModalActionText = ref("")
const error = ref()
const isListModalOpen = ref(false)
const haveFiltersAdded = ref(false)

const saveFilterOptions = [
    {
        name: "Save as New List",
        action: "saveList",
    },
]

const templateTypes = [
    { label: "Text Message", value: "sms" },
    { label: "Email", value: "email" },
]

const cachedTags = computed(() => tagStore.cachedTags)

const selectedCount = computed(() => {
    return templateStore.tableState.isAllSelected
        ? templateStore.tableState.total - templateStore.tableState?.selectedData?.length
        : templateStore.tableState?.selectedData?.length
})

const collections = computed(() =>
    gridCollectionStore.collectionTableState.data.filter((collection) => collection?.isPinned)
)
const grid = computed(() => gridCollectionStore.grid)
const prototype = computed(() => configStore.getPrototype("template"))

const isNotEditableByUser = computed(() => hasUserOnlyReadPermission(route.name as string))

const specialVariable = ref("")

function closeCreateTemplateModal() {
    emit("closeModal")
}

function closePreview() {
    emit("closePreview")
}

function setEmailInputFocus(isFocused: boolean) {
    isEmailBodyFocused.value = isFocused
}

function addMergeFieldToEmail(mergeFieldName: string) {
    // quill.value.updateContents(new Quill.Delta().insert(`{{${mergeFieldName}}}`))
    // Have to manually update the model value of the body, because no event will be emitted
    if (!isEmailBodyFocused.value) {
        tempVarForSubject.value = ""
        tempVarForSubject.value = mergeFieldName
        return
    }
    if (isViewingFormatted.value) {
        const currentSelection = quill.value?.getSelection(true)
        quill.value?.insertText(currentSelection?.index || -1, mergeFieldName)
        localNewTemplate.value.body = quill.value?.root?.innerHTML ?? ""
        return
    }
    addMergeFieldToTextMessage(mergeFieldName)
}

function addMergeFieldToTextMessage(mergeFieldName: string) {
    tempVarForBodyText.value = ""
    tempVarForBodyText.value = mergeFieldName
}

function onEditorLoad(editor: Quill) {
    quill.value = editor
    editor.on("selection-change", (range) => {
        if (range) {
            lastSelection = range
        }
    })
    editor.on("text-change", (delta) => {
        if (typeof delta.ops[0]?.insert === "string") {
            lastSelection.index += delta.ops[0]?.insert?.length
        }
    })
}

function disableValidationError() {
    templateStore.validationError = {} as GgmsError
}

function setEmojiInTextarea(emoji: string) {
    tempVarForBodyText.value = ""
    tempVarForBodyText.value = emoji
}

function openDeleteTemplateModal() {
    emit("openDelete")
}

function openPreview() {
    const models = {
        lead: {} as Lead,
        agency: agency.value as Agency,
        agent: {} as Agent,
    }
    const theme = getTheme() || ""
    let signature = configStore?.signature?.body || ""

    if (isTemplateTypeEmail.value && localNewTemplate.value.body) {
        const emailPreviewBody = localNewTemplate.value.body
        emailPreviewText.value = renderTemplate(emailPreviewBody, configStore.prototypes, models, theme, signature)
        emailPreviewSubject.value = localNewTemplate.value.subject
    }

    const messagePreviewBody =
        isTemplateTypeEmail.value && localNewTemplate.value.body
            ? stripHtml(localNewTemplate.value.body)
            : localNewTemplate.value.textBody

    signature = stripHtml(signature)

    messagePreviewText.value = renderTemplate(messagePreviewBody, configStore.prototypes, models, theme, signature)

    emit("openPreview")
}

function getTheme() {
    if (isTemplateTypeEmail.value && isViewingFormatted.value) {
        return getBodyOfMessageTheme("RichEmail")
    }
    if (isTemplateTypeEmail.value && !isViewingFormatted.value) {
        return getBodyOfMessageTheme("PlainTextEmail")
    }
    return getBodyOfMessageTheme("TextMessage")
}

function getBodyOfMessageTheme(channel: string) {
    return messageThemeStore.messageThemes.find((theme) => theme.channel === channel)?.body
}
async function addNewTemplate() {
    try {
        const templatePayload: TemplatePayload = {
            displayName: localNewTemplate.value.displayName as string,
            templateType: localNewTemplate.value.templateType,
            body:
                isViewingFormatted.value && isTemplateTypeEmail.value
                    ? localNewTemplate.value.body
                    : localNewTemplate.value.textBody,
        }
        if (isTemplateTypeEmail.value) {
            templatePayload.subject = localNewTemplate.value.subject
        }

        if (localSelectedDomains.value.length) {
            templatePayload.domainsIds = localSelectedDomains.value.map((domain) => domain._id)
        }

        templatePayload.tags = localSelectedTags.value

        const response = props.isCreateTemplate
            ? await templateStore.createTemplate(templatePayload)
            : await templateStore.updateTemplate(localNewTemplate.value._id, templatePayload)

        if (!response) {
            return
        }

        if (!props.isCreateTemplate) {
            await templateStore.getTemplates()
        }

        emit("saveTemplate")
        closeCreateTemplateModal()
    } catch (error) {
        console.log(error)
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GgmsModal, {
    open: _ctx.open,
    onCloseModal: closeCreateTemplateModal
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(DocumentTextIcon), { class: "h-6 w-6 text-primary-color-600" })
        ]),
        _createElementVNode("h1", {
          class: "text-lg leading-6 font-medium last:text-gray-900 cursor-pointer last:cursor-auto",
          onClick: closePreview
        }, _toDisplayString(createModalTitle.value) + " Template ", 1),
        (_ctx.isPreviewOpen)
          ? (_openBlock(), _createElementBlock("h1", _hoisted_3, " > Preview"))
          : _createCommentVNode("", true)
      ])
    ]),
    under: _withCtx(() => [
      (!_ctx.isPreviewOpen)
        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
            (!_ctx.isCreateTemplate && _ctx.showDeleteButton)
              ? (_openBlock(), _createBlock(GgmsButton, {
                  key: 0,
                  styleType: "active",
                  disabled: isNotEditableByUser.value,
                  classes: [
                        'text-red-600 bg-transparent border-transparent shadow-transparent focus:ring-red-600/50',
                    ],
                  onClick: openDeleteTemplateModal
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Delete ")
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_21, [
              _createVNode(GgmsButton, {
                styleType: "secondary",
                class: "mr-3",
                onClick: closeCreateTemplateModal
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Cancel")
                ]),
                _: 1
              }),
              _createVNode(GgmsButton, {
                disabled: _unref(templateStore).tableState.isLoading || isNotEditableByUser.value,
                onClick: _cache[14] || (_cache[14] = ($event: any) => (addNewTemplate()))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(createModalButtonText.value), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_22, [
            _createVNode(GgmsButton, { onClick: closePreview }, {
              default: _withCtx(() => [
                _createTextVNode("Close Preview")
              ]),
              _: 1
            })
          ]))
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        (!_ctx.isPreviewOpen)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(GgmsInput, {
                  modelValue: localNewTemplate.value.displayName,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localNewTemplate.value.displayName) = $event)),
                  name: "templateName",
                  label: "Name",
                  placeholder: "Name",
                  "error-message": _unref(hasErrorMessage)('displayname', _unref(templateStore).validationError),
                  invalid: _unref(isInvalid)('displayname', _unref(templateStore).validationError),
                  class: "w-1/2"
                }, null, 8, ["modelValue", "error-message", "invalid"]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(GgmsDropdown, {
                    modelValue: localNewTemplate.value.templateType,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localNewTemplate.value.templateType) = $event)),
                    options: templateTypes,
                    disabled: _ctx.typeDropdownDisabled,
                    label: "Type",
                    optionLabel: "label",
                    optionValue: "value",
                    placeholder: "Select Template Type",
                    class: "w-full"
                  }, null, 8, ["modelValue", "disabled"])
                ])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  (_ctx.domains.length)
                    ? (_openBlock(), _createBlock(GgmsMultiSelect, {
                        key: 0,
                        modelValue: localSelectedDomains.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localSelectedDomains).value = $event)),
                        options: _ctx.domains,
                        label: "Domains",
                        placeholder: "Select Domains",
                        optionLabel: "url",
                        disabled: _ctx.isDomainsDisabled,
                        invalid: _unref(isInvalid)('domains', _unref(templateStore).validationError),
                        errorMessage: _unref(hasErrorMessage)('domains', _unref(templateStore).validationError),
                        classes: "w-full"
                      }, null, 8, ["modelValue", "options", "disabled", "invalid", "errorMessage"]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(GgmsMultiSelect, {
                    modelValue: localSelectedTags.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localSelectedTags).value = $event)),
                    options: _ctx.tags,
                    label: "Tags",
                    placeholder: "Select Tags",
                    display: "chip",
                    optionLabel: "displayName",
                    classes: "w-full"
                  }, null, 8, ["modelValue", "options"])
                ])
              ]),
              (isTemplateTypeEmail.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createVNode(GgmsInput, {
                      modelValue: localNewTemplate.value.subject,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((localNewTemplate.value.subject) = $event)),
                      name: "templateName",
                      label: "Subject",
                      placeholder: "Subject",
                      "template-string": tempVarForSubject.value,
                      "error-message": _unref(hasErrorMessage)('subject', _unref(templateStore).validationError),
                      invalid: _unref(isInvalid)('subject', _unref(templateStore).validationError),
                      onClick: _cache[5] || (_cache[5] = ($event: any) => (setEmailInputFocus(false)))
                    }, null, 8, ["modelValue", "template-string", "error-message", "invalid"]),
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("div", _hoisted_12, [
                        _createVNode(GgmsToggle, {
                          modelValue: isViewingFormatted.value,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((isViewingFormatted).value = $event)),
                          "truth-label": "Visual",
                          "false-label": "Text"
                        }, null, 8, ["modelValue"]),
                        _createVNode(GgmsMergeFields, {
                          specialVariable: specialVariable.value,
                          isTemplateTypeEmail: true,
                          onAddMergeField: addMergeFieldToEmail
                        }, null, 8, ["specialVariable"])
                      ]),
                      _createElementVNode("div", {
                        onClick: _cache[9] || (_cache[9] = ($event: any) => (setEmailInputFocus(true)))
                      }, [
                        (isViewingFormatted.value)
                          ? (_openBlock(), _createBlock(GgmsRichTextEditor, {
                              key: 0,
                              modelValue: localNewTemplate.value.body,
                              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((localNewTemplate.value.body) = $event)),
                              placeholder: "Write your email...",
                              "error-message": _unref(hasErrorMessage)('body', _unref(templateStore).validationError),
                              onLoad: onEditorLoad,
                              onDisableValidationError: disableValidationError
                            }, null, 8, ["modelValue", "error-message"]))
                          : (_openBlock(), _createBlock(GgmsTextarea, {
                              key: 1,
                              modelValue: localNewTemplate.value.textBody,
                              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((localNewTemplate.value.textBody) = $event)),
                              textAreaClasses: "min-h-[244px]",
                              placeholder: "Write your email...",
                              "template-string": tempVarForBodyText.value,
                              "error-message": _unref(hasErrorMessage)('body', _unref(templateStore).validationError),
                              invalid: _unref(isInvalid)('body', _unref(templateStore).validationError),
                              onDisableValidationError: disableValidationError
                            }, null, 8, ["modelValue", "template-string", "error-message", "invalid"]))
                      ])
                    ])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    _createVNode(GgmsMergeFields, {
                      class: "w-fit self-end",
                      onAddMergeField: addMergeFieldToTextMessage
                    }),
                    _createVNode(GgmsTextarea, {
                      modelValue: localNewTemplate.value.textBody,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((localNewTemplate.value.textBody) = $event)),
                      textAreaClasses: "min-h-[244px]",
                      placeholder: "Write your text message...",
                      "template-string": tempVarForBodyText.value,
                      "is-count-available": true,
                      "error-message": _unref(hasErrorMessage)('body', _unref(templateStore).validationError),
                      invalid: _unref(isInvalid)('body', _unref(templateStore).validationError),
                      onDisableValidationError: disableValidationError
                    }, null, 8, ["modelValue", "template-string", "error-message", "invalid"]),
                    _createVNode(GgmsEmojiPicker, {
                      isAlignedTop: true,
                      onSendEmoji: setEmojiInTextarea
                    })
                  ])),
              _createVNode(GgmsButton, {
                styleType: "secondary",
                disabled: !isPreviewAvailable.value,
                "icon-start": _unref(ExternalLinkIcon),
                onClick: openPreview,
                class: "w-fit"
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Preview")
                ]),
                _: 1
              }, 8, ["disabled", "icon-start"])
            ], 64))
          : (_openBlock(), _createElementBlock("div", _hoisted_14, [
              (isTemplateTypeEmail.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    _createVNode(GgmsToggle, {
                      modelValue: isViewingFormatted.value,
                      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((isViewingFormatted).value = $event)),
                      "truth-label": "Visual",
                      "false-label": "Text",
                      class: "w-fit"
                    }, null, 8, ["modelValue"]),
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("p", _hoisted_17, " Subject:   " + _toDisplayString(emailPreviewSubject.value), 1),
                      (isViewingFormatted.value)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            innerHTML: emailPreviewText.value,
                            class: "py-2.5 px-3 border border-gray-300 rounded-md"
                          }, null, 8, _hoisted_18))
                        : (_openBlock(), _createBlock(GgmsTextarea, {
                            key: 1,
                            modelValue: messagePreviewText.value,
                            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((messagePreviewText).value = $event)),
                            readonly: true,
                            textAreaClasses: "min-h-[244px]"
                          }, null, 8, ["modelValue"]))
                    ])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createVNode(GgmsTextarea, {
                      modelValue: messagePreviewText.value,
                      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((messagePreviewText).value = $event)),
                      readonly: true,
                      textAreaClasses: "min-h-[244px]"
                    }, null, 8, ["modelValue"])
                  ]))
            ]))
      ])
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})